import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';

import FooterLink from './FooterLink';

import debounce from '../../helpers/debounce';

import { SUPPORT_LINK } from '../../helpers/constants';
import '../../style/footer.css';

const initialLinkState = {
  displayedLinks: [
    { label: 'Knowledge Center', url: SUPPORT_LINK.KNOWLEDGE_CENTER },
    { label: 'Tips for Guests', url: SUPPORT_LINK.GREEN_ROOM_EXPERIENCE },
    { label: 'Support', url: SUPPORT_LINK.SUPPORT_CENTER },
    { label: 'Product Updates', url: SUPPORT_LINK.PRODUCT_UPDATES },
  ],
  popOverLinks: [],
};

export default function Footer() {
  const [links, setLinks] = useState(() => initialLinkState);
  const [calculating, setCalculating] = useState(false);

  // popover logic
  const [popperOpen, setPopperOpen] = useState(false);

  const linkIconRef = useRef(null);
  const popperRef = useRef(null);

  useEffect(() => {
    const debouncedRevealLinks = debounce(() => setCalculating(false), 150);

    function resetLinks() {
      setPopperOpen(false);
      setCalculating(true);
      setLinks(initialLinkState);
      debouncedRevealLinks();
    }

    function handleDocumentClick(event) {
      if (!event.target.id.includes('pop-over-link')) {
        setPopperOpen(false);
      }
    }

    const debouncedResize = debounce(resetLinks, 200);

    document.addEventListener('mousedown', handleDocumentClick);
    window.addEventListener('resize', debouncedResize);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  const { styles, update } = usePopper(linkIconRef.current, popperRef.current, {
    placement: 'top-start',
    modifiers: [
      {
        name: 'computeStyles',
        options: {
          adaptive: false,
        },
      },
    ],
  });
  // end: popover logic

  const handleOverflow = useCallback(
    (link) => {
      setLinks({
        displayedLinks: links.displayedLinks.filter((l) => l.url !== link),
        popOverLinks: [...links.popOverLinks, links.displayedLinks.find((l) => l.url === link)],
      });
    },
    [links]
  );

  const lastLink = links.displayedLinks?.[links.displayedLinks.length - 1];

  return (
    <div className="footer">
      <a
        href="https://www.socialive.us/"
        id="qaidLogoFooterLink"
        target="_blank"
        rel="noreferrer"
        className="footer-logo-container"
      >
        <img src="/sl_logo_name_v2_light.png" className="footer-logo" alt="footer-logo" />
      </a>
      <div className={`footer-links-container ${calculating ? 'footer-links-invisible' : ''}`}>
        {links.displayedLinks.map((linkObject) => (
          <FooterLink
            key={linkObject.label}
            url={linkObject.url}
            label={linkObject.label}
            lastLink={lastLink}
            handleOverflow={handleOverflow}
          />
        ))}
      </div>
      <button
        type="button"
        className={classNames('footer-links-icon icon-triangle-down', {
          hide: links.popOverLinks.length === 0,
          'footer-links-invisible': calculating,
        })}
        onClick={() => {
          setPopperOpen(!popperOpen);
          update();
        }}
      >
        <i id="pop-over-link-icon" ref={linkIconRef} />
      </button>
      <div ref={popperRef} style={styles.popper} className="pop-over-links">
        <div style={{ display: popperOpen ? 'inline' : 'none' }} visible={popperOpen.toString()}>
          <div id="pop-over-link-container">
            {links.popOverLinks.map((l) => (
              <a
                id="pop-over-link"
                key={l.url}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setPopperOpen(false)}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* This div is to account for the intercom icon */}
      <div className="hidden-footer-div" />
    </div>
  );
}
